import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { migrateTableColumnPrefs } from "@/lib/utils/tanstack-table-local-storage";
import {
  APPROACHING_STATUTES_COLUMN_ANCHORS,
  REPORT_CLASSIFICATION_COLUMN_ALIASES,
  REPORT_CLASSIFICATION_COLUMN_ANCHORS,
} from "@/lib/utils/reports/report-classification-columns";
import { type ReportFilterPreset } from "@/lib/types";
import { type VisibilityState } from "@tanstack/react-table";

export type ReportPresetPreviewFilter = {
  description?: string;
  label: string;
};

export const normalizePresetStringArray = (value: unknown) =>
  Array.isArray(value)
    ? value
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim())
        .filter(Boolean)
    : typeof value === "string"
      ? value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

export const getStoredTableState = (tableName: string) => {
  if (typeof window === "undefined") return undefined;
  const value = window.localStorage.getItem(tableName);
  if (!value) return undefined;

  try {
    const prefs = JSON.parse(value) as {
      columnOrder?: string[];
      columnVisibility?: VisibilityState;
      pagination?: { pageSize?: number };
      sorting?: Array<{ desc: boolean; id: string }>;
    };
    return migrateTableColumnPrefs(
      prefs,
      REPORT_CLASSIFICATION_COLUMN_ALIASES,
      tableName === tanstackTableNames.reportsApproachingStatutes
        ? APPROACHING_STATUTES_COLUMN_ANCHORS
        : REPORT_CLASSIFICATION_COLUMN_ANCHORS,
    );
  } catch {
    return undefined;
  }
};

export const updateStoredTableVisibility = (
  tableName: string,
  columnVisibility: VisibilityState,
) => {
  if (typeof window === "undefined") return;
  const existingState = getStoredTableState(tableName) ?? {};
  window.localStorage.setItem(
    tableName,
    JSON.stringify({
      ...existingState,
      columnVisibility,
    }),
  );
};

export const updateStoredTableColumns = ({
  columnOrder,
  columnVisibility,
  tableName,
}: {
  columnOrder: string[];
  columnVisibility: VisibilityState;
  tableName: string;
}) => {
  if (typeof window === "undefined") return;
  const existingState = getStoredTableState(tableName) ?? {};
  window.localStorage.setItem(
    tableName,
    JSON.stringify({
      ...existingState,
      columnOrder,
      columnVisibility,
    }),
  );
};

export const getOrderedTableColumnIds = ({
  availableColumnIds,
  tableName,
}: {
  availableColumnIds: string[];
  tableName: string;
}) => {
  const storedColumnOrder = getStoredTableState(tableName)?.columnOrder ?? [];
  const availableColumnIdsSet = new Set(availableColumnIds);
  const orderedStoredColumnIds = storedColumnOrder.filter(
    (columnId): columnId is string =>
      typeof columnId === "string" && availableColumnIdsSet.has(columnId),
  );
  const orderedStoredColumnIdsSet = new Set(orderedStoredColumnIds);

  return [
    ...orderedStoredColumnIds,
    ...availableColumnIds.filter(
      (columnId) => !orderedStoredColumnIdsSet.has(columnId),
    ),
  ];
};

export const getPresetOptionValue = (
  preset: ReportFilterPreset,
  index: number,
) =>
  preset.reportFilterPresetId ??
  `${preset.reportFilterPresetName ?? "preset"}-${index}`;

export const getPresetFilterOptions = (
  preset: ReportFilterPreset,
): Record<string, unknown> => {
  const rawValue = preset.reportFilterPresetFilterOptions;
  if (typeof rawValue === "string") {
    try {
      const parsed = JSON.parse(rawValue) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
    return {};
  }
  if (!rawValue || typeof rawValue !== "object" || Array.isArray(rawValue)) {
    return {};
  }

  return rawValue;
};

export const toReportPresetPreviewFilter = (
  value: string,
): ReportPresetPreviewFilter => {
  const separatorIndex = value.indexOf(":");
  if (separatorIndex === -1) {
    return { label: value.trim() };
  }

  const label = value.slice(0, separatorIndex).trim();
  const description = value.slice(separatorIndex + 1).trim();

  return {
    label: label || value.trim(),
    description: description || undefined,
  };
};

export const buildPresetColumnVisibility = ({
  availableColumnIds,
  lockedVisibleColumnIds = [],
  presetColumns,
  presetKeyByColumnId,
}: {
  availableColumnIds: string[];
  lockedVisibleColumnIds?: string[];
  presetColumns: unknown;
  presetKeyByColumnId: Record<string, string>;
}) => {
  const visiblePresetKeys = new Set(normalizePresetStringArray(presetColumns));
  const lockedVisibleColumnIdsSet = new Set(lockedVisibleColumnIds);

  return availableColumnIds.reduce<VisibilityState>((acc, columnId) => {
    if (lockedVisibleColumnIdsSet.has(columnId)) {
      acc[columnId] = true;
      return acc;
    }

    const presetKey = presetKeyByColumnId[columnId];
    acc[columnId] = presetKey ? visiblePresetKeys.has(presetKey) : true;
    return acc;
  }, {});
};

export const buildPresetColumnsFromVisibility = ({
  availableColumnIds,
  defaultColumnVisibility = {},
  presetKeyByColumnId,
  tableName,
}: {
  availableColumnIds: string[];
  defaultColumnVisibility?: VisibilityState;
  presetKeyByColumnId: Record<string, string>;
  tableName: string;
}) => {
  const columnVisibility = {
    ...defaultColumnVisibility,
    ...(getStoredTableState(tableName)?.columnVisibility ?? {}),
  };
  const orderedColumnIds = getOrderedTableColumnIds({
    availableColumnIds,
    tableName,
  });

  return orderedColumnIds
    .filter((columnId) => columnVisibility[columnId] ?? true)
    .map((columnId) => presetKeyByColumnId[columnId])
    .filter(Boolean);
};

export const buildPresetColumnOrder = ({
  availableColumnIds,
  presetColumns,
  presetKeyByColumnId,
  tableName,
}: {
  availableColumnIds: string[];
  presetColumns: unknown;
  presetKeyByColumnId: Record<string, string>;
  tableName: string;
}) => {
  const presetColumnIdsByKey = Object.entries(presetKeyByColumnId).reduce<
    Record<string, string>
  >((acc, [columnId, presetKey]) => {
    acc[presetKey] = columnId;
    return acc;
  }, {});
  const orderedPresetColumnIds: string[] = [];
  const orderedPresetColumnIdsSet = new Set<string>();

  normalizePresetStringArray(presetColumns).forEach((presetKey) => {
    const columnId = presetColumnIdsByKey[presetKey];
    if (!columnId || orderedPresetColumnIdsSet.has(columnId)) return;

    orderedPresetColumnIds.push(columnId);
    orderedPresetColumnIdsSet.add(columnId);
  });

  const orderedExistingColumnIds = getOrderedTableColumnIds({
    availableColumnIds,
    tableName,
  });

  return [
    ...orderedPresetColumnIds,
    ...orderedExistingColumnIds.filter(
      (columnId) => !orderedPresetColumnIdsSet.has(columnId),
    ),
  ];
};
